import {BrandType} from '../../../../../provider/brands/brand-type';
import {CompaniesSelectorFilterType} from '../../../../../service/feature-companies/feature-companies-type';

// A story never mixes brands, so the bulk actions of the step have to know which brand the list is
// narrowed to: the brand filter, or the only brand the account has.
export function getBrandScopeId(filter: CompaniesSelectorFilterType, brands: Array<BrandType>): number | null {
    return filter.brandId || (brands.length === 1 ? brands[0]?.id : null) || null;
}

type AutoAddAvailabilityOptionsType = {
    filter: CompaniesSelectorFilterType;
    brandScopeId: number | null;
    activeBrandId: number | null;
    filteredCount: number;
    selectedCount: number;
};

// Auto-add only makes sense when the whole brand is picked — the one case the backend's sync will
// not silently widen the story. `selectedCount` covers the whole selector while `filteredCount`
// follows the filter, so both have to describe the same brand before they can be compared: without
// that check a brand filtered to the same size as an unrelated selection would enable the switch.
export function checkIsAutoAddAvailable(options: AutoAddAvailabilityOptionsType): boolean {
    const {filter, brandScopeId, activeBrandId, filteredCount, selectedCount} = options;

    if (!brandScopeId || brandScopeId !== activeBrandId) {
        return false;
    }

    if (filter.q || filter.companyGroupId || filter.selectedOnly) {
        return false;
    }

    return filteredCount > 0 && selectedCount === filteredCount;
}

type SelectAllStateOptionsType = {
    filter: CompaniesSelectorFilterType;
    activeBrandId: number | null;
    isInProgress: boolean;
    filteredCount: number;
    selectedCount: number;
};

// The header checkbox acts on everything the filter matches, so its state is counted over the whole
// selector instead of over the loaded page — otherwise it would read as "all selected" as soon as
// the first page of a large brand is picked. It stays disabled while the brand is still undecided
// (a multi-brand account with no brand filter and nothing selected yet) and while the filter points
// at a brand the story is not bound to, because neither list can be selected as a whole.
export function getSelectAllState(options: SelectAllStateOptionsType): {
    isChecked: boolean;
    isDisabled: boolean;
    isIndeterminate: boolean;
} {
    const {filter, activeBrandId, isInProgress, filteredCount, selectedCount} = options;

    const hasForeignBrandFilter = Boolean(filter.brandId && filter.brandId !== activeBrandId);

    if (!activeBrandId || hasForeignBrandFilter) {
        return {isChecked: false, isDisabled: true, isIndeterminate: false};
    }

    const isEverythingSelected = filteredCount > 0 && selectedCount >= filteredCount;

    return {
        isChecked: isEverythingSelected,
        isDisabled: isInProgress,
        isIndeterminate: !isEverythingSelected && selectedCount > 0,
    };
}
